import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Select,
  Tag,
  Tooltip,
  type UploadFile
} from 'antd'
import { Check, History, Lock, Pencil } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { LanguageEnum, Organization } from 'src/api/main'
import { ConfirmActionModal, DomainNameFormItem } from 'src/components'
import { OrganizationAvatar } from 'src/features/organizations/components/OrganizationSettings/OrganizationAvatar'
import { allLangs } from 'src/locales'

const LANGUAGE_OPTIONS = allLangs.map((lang) => ({
  value: lang.value,
  label: lang.label
}))

export type GeneralSettingsFormValues = {
  name: string
  domain_name: string
  description?: string
  photo?: UploadFile[]
  default_language: LanguageEnum
  enabled_languages: LanguageEnum[]
}

type Props = {
  organization: Organization
  onSave: (values: GeneralSettingsFormValues) => void
  loading?: boolean
}

export const GeneralSettingsSection = ({
  organization,
  onSave,
  loading
}: Props) => {
  const { t } = useTranslation()
  const [form] = Form.useForm<GeneralSettingsFormValues>()
  const [domainUnlocked, setDomainUnlocked] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)

  // The default language can only be one that is currently enabled, so its
  // options track the live enabled-languages selection.
  const enabledLanguages = Form.useWatch('enabled_languages', form)
  const defaultLanguageOptions = enabledLanguages?.length
    ? LANGUAGE_OPTIONS.filter((option) =>
        enabledLanguages.includes(option.value)
      )
    : LANGUAGE_OPTIONS

  const initialValues: GeneralSettingsFormValues = {
    name: organization.name,
    domain_name: organization.domain_name,
    description: '',
    default_language: organization.default_language ?? allLangs[0].value,
    enabled_languages: organization.enabled_languages?.length
      ? organization.enabled_languages
      : allLangs.map((lang) => lang.value),
    photo: organization.photo
      ? [
          {
            uid: String(organization.photo.id),
            name: organization.photo.title,
            status: 'done',
            url: organization.photo.file,
            thumbUrl: organization.photo.file,
            response: organization.photo
          }
        ]
      : []
  }

  const handleSubmit = (values: GeneralSettingsFormValues) => {
    // Defence-in-depth: only forward the domain field when the user has
    // explicitly unlocked it via the confirmation modal. Otherwise reset to
    // the saved value before submission.
    onSave({
      ...values,
      domain_name: domainUnlocked
        ? values.domain_name
        : organization.domain_name
    })
  }

  return (
    <>
      <Card
        variant="outlined"
        title={t('workspace.settings.general')}
        extra={
          <Button
            size="small"
            icon={<History size={14} />}
            disabled
            title={t('workspace.settings.auditChangesPreview')}
          >
            {t('workspace.settings.auditChanges')}
          </Button>
        }
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={initialValues}
          onFinish={handleSubmit}
        >
          <OrganizationAvatar organization={organization} />

          <Row gutter={14}>
            <Col xs={24} md={10}>
              <Form.Item
                name="name"
                label={t('workspace.settings.workspaceName')}
                rules={[{ required: true, message: t('required_field_error') }]}
              >
                <Input placeholder="Skalx" />
              </Form.Item>
            </Col>
            <Col xs={24} md={14}>
              <DomainNameFormItem
                label={t('workspace.settings.domain')}
                inputProps={{
                  size: 'middle',
                  disabled: !domainUnlocked,
                  suffix: domainUnlocked ? null : (
                    <Tooltip title={t('workspace.settings.domainLockedHint')}>
                      <Lock size={12} color="var(--ant-color-text-tertiary)" />
                    </Tooltip>
                  )
                }}
              />
              {domainUnlocked ? (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginTop: -8, marginBottom: 12 }}
                  title={t('workspace.settings.domainUnlockedNotice')}
                  action={
                    <Button
                      size="small"
                      onClick={() => {
                        form.setFieldValue(
                          'domain_name',
                          organization.domain_name
                        )
                        setDomainUnlocked(false)
                      }}
                    >
                      {t('cancel')}
                    </Button>
                  }
                />
              ) : (
                <Button
                  size="small"
                  type="link"
                  icon={<Pencil size={12} />}
                  onClick={() => setConfirmOpen(true)}
                  style={{ paddingInline: 0, marginTop: -8 }}
                >
                  {t('workspace.settings.changeDomain')}
                </Button>
              )}
            </Col>
            <Col span={24}>
              <Form.Item
                name="description"
                label={t('workspace.settings.descriptionLabel')}
                extra={t('workspace.settings.descriptionHelp')}
              >
                <Input.TextArea
                  rows={2}
                  maxLength={200}
                  showCount
                  placeholder={t('workspace.settings.descriptionPlaceholder')}
                  disabled
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={14}>
              <Form.Item
                name="enabled_languages"
                label={t('workspace.settings.languages.enabled')}
                extra={t('workspace.settings.languages.enabledHelp')}
                rules={[
                  {
                    validator: (_rule, value: LanguageEnum[] | undefined) =>
                      value && value.length > 0
                        ? Promise.resolve()
                        : Promise.reject(
                            new Error(
                              t('workspace.settings.languages.enabledRequired')
                            )
                          )
                  }
                ]}
              >
                <Select
                  mode="multiple"
                  options={LANGUAGE_OPTIONS}
                  placeholder={t('workspace.settings.languages.placeholder')}
                  onChange={(next) => {
                    // Drop the default when it leaves the enabled set so the
                    // dependent field re-validates instead of holding a stale
                    // value the backend would reject.
                    const enabled = next as LanguageEnum[]
                    const currentDefault = form.getFieldValue(
                      'default_language'
                    ) as LanguageEnum | undefined
                    if (currentDefault && !enabled.includes(currentDefault)) {
                      form.setFieldValue('default_language', undefined)
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={10}>
              <Form.Item
                name="default_language"
                label={t('workspace.settings.languages.default')}
                extra={t('workspace.settings.languages.defaultHelp')}
                dependencies={['enabled_languages']}
                rules={[
                  { required: true, message: t('required_field_error') },
                  ({ getFieldValue }) => ({
                    validator: (_rule, value: LanguageEnum | undefined) => {
                      const enabled = (getFieldValue('enabled_languages') ??
                        []) as LanguageEnum[]

                      return !value || enabled.includes(value)
                        ? Promise.resolve()
                        : Promise.reject(
                            new Error(
                              t(
                                'workspace.settings.languages.defaultMustBeEnabled'
                              )
                            )
                          )
                    }
                  })
                ]}
              >
                <Select options={defaultLanguageOptions} />
              </Form.Item>
            </Col>
          </Row>
          <Row justify="end" align="middle">
            <Tag color="default" style={{ marginInlineEnd: 8 }}>
              {t('workspace.settings.savedFields')}
            </Tag>
            <Button
              type="primary"
              htmlType="submit"
              icon={<Check size={14} />}
              loading={loading}
            >
              {t('workspace.settings.save')}
            </Button>
          </Row>
        </Form>
      </Card>

      <ConfirmActionModal
        open={confirmOpen}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false)
          setDomainUnlocked(true)
        }}
        title={t('workspace.settings.domainConfirmTitle')}
        description={t('workspace.settings.domainConfirmDescription', {
          name: organization.name
        })}
        warningMessage={t('workspace.settings.domainConfirmWarning')}
        confirmText={organization.domain_name}
        confirmButtonText={t('workspace.settings.domainConfirmButton')}
        confirmPlaceholder={organization.domain_name}
        danger={false}
      />
    </>
  )
}
